/**
 * MiniProjectScheduleTab — Task #229.
 *
 * Body for the Schedule sub-tab inside MiniProjectKpiNavigation. Built
 * top-to-bottom as a single mental model: macro schedule signal at the
 * top, the time-series chart in the middle, and the granular Tasks
 * section at the bottom (the work that actually drives the % numbers).
 *
 * Top — "Work completed" bar
 *   Eyebrow + 10px progress bar in the schedule color, with
 *   Start: <date> | <pct>% | Finish: <date> beneath. Same pattern FP
 *   uses on the Schedule sub-tab of KpiNavigation (lines 977–1019). MP
 *   sources actualPct from the latest MiniProjectStatusSummary, color
 *   from scheduleColor (already computed by the parent's
 *   computeKpiStatus), and start/finish from the MiniProject row.
 *
 * Middle — Planned vs actual chart
 *   SVG line chart lifted from ScheduleModal, adapted for inline width
 *   and stripped of modal chrome. Per Skip 2026-05-27 two elements
 *   from the modal were dropped: the recent-updates table (duplicated
 *   the chart in tabular form) and the snapshot row of planned/actual/
 *   as-of stats (redundant with the Work Completed bar above — that
 *   bar already carries the headline actual % plus start/finish
 *   dates). The threshold legend stays as a single line below the
 *   chart.
 *
 *   The chart SVG mirrors ScheduleModal's hand-rolled implementation
 *   rather than sharing a component. Skip plans to deprecate the
 *   ScheduleModal once all four KPI sub-tabs are built (#229–#232),
 *   so the modal stays untouched in this pass and this file owns the
 *   chart code going forward.
 *
 * Bottom — Tasks section
 *   Section header with "+" button (Lead only) + TasksSection
 *   (sections/TasksSection.tsx). TasksSection is unchanged from the
 *   magazine-spread era — it already takes a tasks[] prop and a
 *   create-tray open/close pair, which the parent (ProgressTab) plumbs
 *   through. Section header reads "Tasks" rather than the magazine-era
 *   "Schedule" because the sub-tab itself is now called Schedule —
 *   otherwise we'd have a "Schedule sub-tab > Schedule section" stutter.
 */

import { useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProject, MiniProjectTask } from "../../api/miniProjects";
import type { MiniProjectStatusSummary } from "../../api/miniProjectStatuses";
import {
  kpiColorHex,
  type KpiColor,
} from "../../Pages/MiniProjectDetail/computeKpiStatus";
import TasksSection from "../../Pages/MiniProjectDetail/sections/TasksSection";
import styles from "./MiniProjectScheduleTab.module.css";

interface Props {
  miniProject: MiniProject;
  statusSummaries: MiniProjectStatusSummary[];
  scheduleColor: KpiColor;
  tasks: MiniProjectTask[];
  isLead: boolean;
  /** Refetch trigger for task mutations (delegates to the parent's
   *  sub.refetch since tasks live on the shared sub-entities hook). */
  onTasksMutated: () => void;
  /** Add-tray open state for Tasks, lifted to the parent so the
   *  section header "+" can trigger it without TasksSection owning
   *  the toggle. */
  addingTask: boolean;
  onOpenAddTask: () => void;
  onCloseAddTask: () => void;
}

/* ── Date helpers ──────────────────────────────────────────────── */

function fmtShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtChartTick(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

export default function MiniProjectScheduleTab({
  miniProject,
  statusSummaries,
  scheduleColor,
  tasks,
  isLead,
  onTasksMutated,
  addingTask,
  onOpenAddTask,
  onCloseAddTask,
}: Props) {
  const { t } = useAppTranslation("pages");

  // Latest summary (server returns newest-first) drives the work-
  // completed bar. The chart pulls from the full summaries array, so
  // the only headline number we extract here is actualPct for the
  // progress bar fill.
  const latest = statusSummaries[0] ?? null;
  const actualPct = latest ? Math.max(0, Math.min(100, Number(latest.actualPct))) : 0;

  const schedColorHex = kpiColorHex(scheduleColor);

  return (
    <div className={styles.root}>
      {/* ── Work completed bar ───────────────────────────────── */}
      <div className={styles.workCompletedCard}>
        <div className={styles.eyebrow}>
          {t("projectDetail.core.workCompleted")}
        </div>
        <div className={styles.barTrack}>
          <div
            className={styles.barFill}
            style={{
              inlineSize: `${actualPct}%`,
              background: schedColorHex,
            }}
          />
        </div>
        <div className={styles.barLegend}>
          <span>
            <b>{t("miniProject.kpiModals.schedule.snapshot.start", { defaultValue: "Start" })}:</b>{" "}
            {fmtShort(miniProject.startDate)}
          </span>
          <span style={{ fontWeight: 600, color: schedColorHex }}>
            {actualPct.toFixed(0)}%
          </span>
          <span>
            <b>{t("miniProject.kpiModals.schedule.snapshot.finish", { defaultValue: "Finish" })}:</b>{" "}
            {fmtShort(miniProject.endDate)}
          </span>
        </div>
      </div>

      {/* ── Tasks section ───────────────────────────────────── */}
      {/* Tasks placed ABOVE the chart (Skip, 2026-06-10): tasks are the
          actionable content; the planned-vs-actual chart is a high-level
          visualization that, on large monitors, was tall enough to shove
          the tasks below the fold. */}
      <div className={styles.tasksCard}>
        <div className={styles.tasksHeader}>
          <div className={styles.eyebrow}>
            {t("miniProject.progress.section.tasks")}
          </div>
          {isLead && (
            <button
              type="button"
              onClick={onOpenAddTask}
              aria-label={t("miniProject.progress.section.addTask")}
              className={styles.addBtn}
            >
              +
            </button>
          )}
        </div>
        <TasksSection
          miniProjectGuid={miniProject.miniProjectGuid}
          tasks={tasks}
          isLead={isLead}
          onMutated={onTasksMutated}
          addOpen={addingTask}
          onCloseAdd={onCloseAddTask}
          layout="split"
        />
      </div>

      {/* ── Planned vs actual chart ─────────────────────────── */}
      {statusSummaries.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>
            {t("miniProject.kpiModals.schedule.empty.title")}
          </div>
          <div className={styles.emptyHint}>
            {t("miniProject.kpiModals.schedule.empty.hint")}
            {isLead
              ? ` ${t("miniProject.kpiModals.schedule.empty.leadCta")}`
              : ` ${t("miniProject.kpiModals.schedule.empty.publicHint")}`}
          </div>
        </div>
      ) : (
        <div className={styles.chartCard}>
          {/* Snapshot row removed 2026-05-27 — the Work Completed bar
              above already shows the headline actual % plus start /
              finish dates, so this row was duplicating data. */}

          {/* Chart */}
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>
              {t("miniProject.kpiModals.schedule.chart.title")}
            </span>
            <span className={styles.chartLegend}>
              <span className={styles.legendItem}>
                <span className={styles.legendDashed} aria-hidden="true" />
                {t("miniProject.kpiModals.schedule.legend.planned")}
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendSolid} aria-hidden="true" />
                {t("miniProject.kpiModals.schedule.legend.actual")}
              </span>
            </span>
          </div>
          <ScheduleChart
            summaries={statusSummaries}
            chartAriaLabel={t("miniProject.kpiModals.schedule.chart.aria")}
          />
          {/* Threshold legend removed 2026-05-27 — Skip's call. The
              snapshot row removal made the threshold rule less
              referenceable inline, and the bar color above the chart
              already telegraphs schedule health at a glance. */}
        </div>
      )}

    </div>
  );
}

/* ── Inline SVG chart ──────────────────────────────────────────────
 *
 * Mirrors the chart from ScheduleModal. Hand-rolled SVG so there's no
 * chart-library dependency; viewBox-driven so the chart scales cleanly
 * to whatever inline width the sub-tab gets.
 *
 * Y axis: 0–100%. X axis: evenly-spaced report dates (not calendar-
 * scaled — keeps the visual stable when filings are sparse). Planned
 * is dashed, actual is solid; matches the modal's visual language so
 * users moving between the two surfaces see the same shape.
 */

const CHART_WIDTH = 560;
const CHART_HEIGHT = 200;
const CHART_PAD_X = 32;
const CHART_PAD_Y = 18;

function ScheduleChart({
  summaries,
  chartAriaLabel,
}: {
  summaries: MiniProjectStatusSummary[];
  chartAriaLabel: string;
}) {
  // Order ascending (oldest left → newest right) for the chart.
  const chrono = useMemo(() => [...summaries].reverse(), [summaries]);

  const { plannedPath, actualPath, xs } = useMemo(() => {
    const n = chrono.length;
    if (n === 0) {
      return { plannedPath: "", actualPath: "", xs: [] as { x: number; iso: string }[] };
    }
    const usableW = CHART_WIDTH - CHART_PAD_X * 2;
    const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
    const stride = n > 1 ? usableW / (n - 1) : 0;
    const plannedPts: string[] = [];
    const actualPts: string[] = [];
    const localXs: { x: number; iso: string }[] = [];
    for (let i = 0; i < n; i++) {
      const s = chrono[i];
      const x = CHART_PAD_X + (n > 1 ? i * stride : usableW / 2);
      const p = Math.max(0, Math.min(100, Number(s.plannedPct)));
      const a = Math.max(0, Math.min(100, Number(s.actualPct)));
      const yP = CHART_PAD_Y + usableH * (1 - p / 100);
      const yA = CHART_PAD_Y + usableH * (1 - a / 100);
      plannedPts.push(`${x.toFixed(1)},${yP.toFixed(1)}`);
      actualPts.push(`${x.toFixed(1)},${yA.toFixed(1)}`);
      localXs.push({ x, iso: s.reportingDate });
    }
    return {
      plannedPath: plannedPts.join(" "),
      actualPath: actualPts.join(" "),
      xs: localXs,
    };
  }, [chrono]);

  // ≤5 evenly-spaced x-axis ticks so labels don't overlap.
  const xTicks = useMemo(() => {
    const n = xs.length;
    if (n === 0) return [];
    if (n <= 5) return xs;
    const step = Math.ceil(n / 5);
    const out: typeof xs = [];
    for (let i = 0; i < n; i += step) out.push(xs[i]);
    if (out[out.length - 1] !== xs[n - 1]) out.push(xs[n - 1]);
    return out;
  }, [xs]);

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className={styles.chart}
      role="img"
      aria-label={chartAriaLabel}
    >
      {/* 0/25/50/75/100% gridlines */}
      {[0, 25, 50, 75, 100].map((pct) => {
        const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
        const y = CHART_PAD_Y + usableH * (1 - pct / 100);
        return (
          <g key={pct}>
            <line
              x1={CHART_PAD_X}
              x2={CHART_WIDTH - CHART_PAD_X}
              y1={y}
              y2={y}
              className={styles.gridline}
            />
            <text
              x={CHART_PAD_X - 6}
              y={y + 3}
              textAnchor="end"
              className={styles.axisLabel}
            >
              {pct}
            </text>
          </g>
        );
      })}

      {plannedPath && (
        <polyline points={plannedPath} fill="none" className={styles.plannedLine} />
      )}
      {actualPath && (
        <polyline points={actualPath} fill="none" className={styles.actualLine} />
      )}

      {/* Single-point fallback — draw dots so one filing is still visible. */}
      {xs.length === 1 && (
        <g>
          {(() => {
            const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
            const x = xs[0].x;
            const p = Math.max(0, Math.min(100, Number(chrono[0].plannedPct)));
            const a = Math.max(0, Math.min(100, Number(chrono[0].actualPct)));
            const yP = CHART_PAD_Y + usableH * (1 - p / 100);
            const yA = CHART_PAD_Y + usableH * (1 - a / 100);
            return (
              <>
                <circle cx={x} cy={yP} r={4} className={styles.plannedDot} />
                <circle cx={x} cy={yA} r={4} className={styles.actualDot} />
              </>
            );
          })()}
        </g>
      )}

      {/* X-axis date labels */}
      {xTicks.map((tk, i) => (
        <text
          key={`${i}-${tk.iso}`}
          x={tk.x}
          y={CHART_HEIGHT - 4}
          textAnchor="middle"
          className={styles.axisLabel}
        >
          {fmtChartTick(tk.iso)}
        </text>
      ))}
    </svg>
  );
}
